import React from 'react';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@artstorefronts/ui';
import { Trash2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import {
  PLATFORM_URL_PREFIXES,
  USERNAME_ONLY_PLATFORMS,
  extractUsernameFromUrl,
  constructUrlFromUsername,
} from '../../utils/socialLinks';

interface SocialLinkFieldProps {
  index: number;
  onRemove: () => void;
  register: any;
  watch: any;
  setValue: any;
  errors: any;
  locked?: boolean;
}

const STATIC_SOCIAL_PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: 'instagram', placeholder: 'https://instagram.com/username' },
  { value: 'x-twitter', label: 'X (Twitter)', icon: 'x-twitter', placeholder: 'https://x.com/username' },
  { value: 'facebook', label: 'Facebook', icon: 'facebook', placeholder: 'https://facebook.com/username' },
  { value: 'youtube', label: 'YouTube', icon: 'youtube', placeholder: 'https://youtube.com/username' },
  { value: 'tiktok', label: 'TikTok', icon: 'tiktok', placeholder: 'https://tiktok.com/@username' },
  { value: 'linkedin', label: 'LinkedIn', icon: 'linkedin', placeholder: 'in/username or company/name' },
  { value: 'pinterest', label: 'Pinterest', icon: 'pinterest', placeholder: 'https://pinterest.com/username' },
  { value: 'bluesky', label: 'Bluesky', icon: 'bluesky', placeholder: 'https://bsky.app/profile/username.bsky.social' },
  { value: 'threads', label: 'Threads', icon: 'threads', placeholder: 'https://threads.net/@username' },
  {
    value: 'website',
    labelKey: 'profile.social-links.platform-website',
    icon: 'globe',
    placeholder: 'https://yourwebsite.com',
  },
  { value: 'other', labelKey: 'profile.social-links.platform-other', icon: 'link', placeholder: 'https://example.com' },
] as const;

export const SocialLinkField: React.FC<SocialLinkFieldProps> = ({
  index,
  onRemove,
  register,
  watch,
  setValue,
  errors,
  locked = false,
}) => {
  const { t } = useTranslation('settings');

  const SOCIAL_PLATFORMS = React.useMemo(
    () =>
      STATIC_SOCIAL_PLATFORMS.map((p) => ({
        ...p,
        label: 'labelKey' in p ? t(p.labelKey) : p.label,
      })),
    [t]
  );

  const selectedPlatform = watch(`socialLinks.${index}.type`);
  const currentUrl = watch(`socialLinks.${index}.url`);
  const currentUsername = watch(`socialLinks.${index}.username`);
  const showDisplayName = selectedPlatform === 'other';
  const isUsernameOnly = selectedPlatform && USERNAME_ONLY_PLATFORMS.has(selectedPlatform);
  const urlPrefix = selectedPlatform ? PLATFORM_URL_PREFIXES[selectedPlatform] || '' : '';

  // Check for errors - react-hook-form errors can be objects with message property or strings
  const urlError = (errors.socialLinks as any)?.[index]?.url;
  const usernameError = (errors.socialLinks as any)?.[index]?.username;
  const hasUrlError = !!urlError;
  const hasUsernameError = !!usernameError;
  const titleError = (errors.socialLinks as any)?.[index]?.title;
  const hasTitleError = !!titleError;

  // Extract username from URL when platform changes or URL changes
  React.useEffect(() => {
    if (isUsernameOnly && selectedPlatform && currentUrl && !currentUsername) {
      const extractedUsername = extractUsernameFromUrl(currentUrl, selectedPlatform);
      if (extractedUsername) {
        setValue(`socialLinks.${index}.username`, extractedUsername, { shouldValidate: false });
      }
    }
  }, [selectedPlatform, currentUrl, isUsernameOnly, currentUsername, index, setValue]);

  const handlePlatformChange = (value: string) => {
    setValue(`socialLinks.${index}.type`, value, { shouldValidate: true });
    // Clear username/url when platform changes
    if (USERNAME_ONLY_PLATFORMS.has(value)) {
      setValue(`socialLinks.${index}.username`, '', { shouldValidate: false });
      setValue(`socialLinks.${index}.url`, '', { shouldValidate: false });
    } else {
      setValue(`socialLinks.${index}.url`, '', { shouldValidate: false });
    }
    // Focus input immediately after platform selection
    setTimeout(() => {
      const inputName = USERNAME_ONLY_PLATFORMS.has(value)
        ? `socialLinks.${index}.username`
        : `socialLinks.${index}.url`;
      const input = document.querySelector(`input[name="${inputName}"]`) as HTMLInputElement;
      if (input && !input.disabled) {
        input.focus();
      }
    }, 50);
  };

  const handleUsernameChange = (value: string) => {
    // For Facebook, check if user is entering a profile.php?id= format
    if (selectedPlatform === 'facebook' && value.startsWith('profile.php?id=')) {
      // User is entering a profile.php URL format - construct the full URL
      const idValue = value.replace('profile.php?id=', '');
      if (idValue) {
        setValue(`socialLinks.${index}.username`, value, { shouldValidate: true });
        setValue(`socialLinks.${index}.url`, `https://facebook.com/profile.php?id=${idValue}`, {
          shouldValidate: true,
        });
      } else {
        setValue(`socialLinks.${index}.username`, value, { shouldValidate: true });
        setValue(`socialLinks.${index}.url`, '', { shouldValidate: true });
      }
      return;
    }

    // Remove @ if user types it
    const cleanedValue = value.replace('@', '');
    setValue(`socialLinks.${index}.username`, cleanedValue, { shouldValidate: true });
    // Auto-construct URL for validation
    if (cleanedValue && selectedPlatform) {
      const constructedUrl = constructUrlFromUsername(cleanedValue, selectedPlatform);
      setValue(`socialLinks.${index}.url`, constructedUrl, { shouldValidate: true });
    } else {
      setValue(`socialLinks.${index}.url`, '', { shouldValidate: true });
    }
  };

  // Get the URL to open - either from currentUrl or construct from username
  const getUrlToOpen = (): string | null => {
    if (!selectedPlatform) return null;

    if (isUsernameOnly) {
      // For Facebook profile.php URLs, use the URL directly if available
      if (selectedPlatform === 'facebook' && currentUrl) {
        try {
          const urlObj = new URL(currentUrl.trim());
          if (urlObj.pathname === '/profile.php' && urlObj.searchParams.has('id')) {
            return currentUrl.trim();
          }
        } catch {
          // If URL parsing fails, continue with normal logic
        }
      }
      if (currentUsername && currentUsername.trim()) {
        const constructedUrl = constructUrlFromUsername(currentUsername.trim(), selectedPlatform);
        return constructedUrl || null;
      }
      return null;
    } else {
      if (currentUrl && currentUrl.trim()) {
        return currentUrl.trim();
      }
      return null;
    }
  };

  const urlToOpen = getUrlToOpen();

  const handleOpenLink = (e: React.MouseEvent) => {
    e.preventDefault();
    if (urlToOpen) {
      window.open(urlToOpen, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={`grid gap-x-3 gap-y-2 p-3 border rounded-lg bg-gray-50 relative ${
        showDisplayName
          ? 'grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto_auto]'
          : 'grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto]'
      }`}
    >
      {/* Platform Select */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <Select value={selectedPlatform} onValueChange={handlePlatformChange} disabled={locked}>
          <SelectTrigger className={`h-9 ${locked ? 'cursor-not-allowed disabled:opacity-100' : ''}`}>
            <SelectValue placeholder={t('profile.social-links.select-platform')} />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {SOCIAL_PLATFORMS.map((platform) => (
              <SelectItem key={platform.value} value={platform.value}>
                <div className="flex items-center gap-2">
                  <Icon icon={platform.icon} size={4} />
                  <span>{platform.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Hidden input for form validation */}
        <input type="hidden" {...register(`socialLinks.${index}.type`)} value={selectedPlatform || ''} />
      </div>

      {/* Username Input (for social platforms) or URL Input (for website/other) */}
      {isUsernameOnly ? (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-0">
            <div className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-sm text-gray-600 whitespace-nowrap">
              {urlPrefix}
            </div>
            <Input
              name={`socialLinks.${index}.username`}
              placeholder={
                selectedPlatform === 'bluesky'
                  ? 'username.bsky.social'
                  : selectedPlatform === 'linkedin'
                    ? 'in/username or company/name'
                    : t('profile.social-links.username-placeholder')
              }
              className={`h-9 rounded-l-none flex-1 ${hasUsernameError ? 'border-red-500' : ''}`}
              disabled={!selectedPlatform || locked}
              {...register(`socialLinks.${index}.username`, {
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  handleUsernameChange(e.target.value);
                },
              })}
            />
          </div>
          {/* Hidden URL field for validation - auto-populated from username */}
          <input type="hidden" {...register(`socialLinks.${index}.url`)} />
        </div>
      ) : (
        <div className="flex-1 min-w-0">
          <Input
            placeholder={SOCIAL_PLATFORMS.find((platform) => platform.value === selectedPlatform)?.placeholder}
            className={`h-9 ${hasUrlError ? 'border-red-500' : ''}`}
            disabled={!selectedPlatform || locked}
            {...register(`socialLinks.${index}.url`)}
          />
        </div>
      )}

      {/* Display Name Input (only for "other" platform) */}
      {showDisplayName && (
        <div className="flex-1 min-w-0">
          <Input
            placeholder={t('profile.social-links.website-name-placeholder')}
            className={`h-9 ${hasTitleError ? 'border-red-500' : ''}`}
            {...register(`socialLinks.${index}.title`)}
          />
        </div>
      )}

      {/* Button Container - keeps buttons together on mobile */}
      <div className="flex items-center gap-2 sm:contents">
        {/* External Link Button - disabled when there's no URL */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleOpenLink}
          disabled={!urlToOpen}
          className="text-black hover:text-gray-700 h-9 w-9 p-0 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          title={urlToOpen ? t('profile.social-links.open-link-title') : t('profile.social-links.no-url-title')}
        >
          <ExternalLink className="w-4 h-4" />
        </Button>

        {/* Remove Button - only show if not locked */}
        {!locked && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 h-9 w-9 p-0 flex-shrink-0"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Error Messages Row */}
      {((errors.socialLinks as any)?.[index]?.type ||
        (errors.socialLinks as any)?.[index]?.url ||
        (errors.socialLinks as any)?.[index]?.username ||
        (errors.socialLinks as any)?.[index]?.title) && (
        <div className="col-span-full mt-2">
          <div className="flex flex-wrap gap-4 text-red-500 text-xs">
            {(errors.socialLinks as any)?.[index]?.type &&
              typeof (errors.socialLinks as any)[index]?.type === 'object' && (
                <span>{(errors.socialLinks as any)[index]?.type?.message}</span>
              )}
            {(errors.socialLinks as any)?.[index]?.url && (
              <span>{(errors.socialLinks as any)[index]?.url?.message}</span>
            )}
            {(errors.socialLinks as any)?.[index]?.username && (
              <span>{(errors.socialLinks as any)[index]?.username?.message}</span>
            )}
            {(errors.socialLinks as any)?.[index]?.title && (
              <span>
                {t('profile.social-links.name-error-prefix')} {(errors.socialLinks as any)[index]?.title?.message}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
